import { AdminService, ExperimentalUser } from '@/utils/apis/admin-service';
import React, { useEffect, useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';

interface Experiment {
  id: string;
  name: string;
  code: string;
  description: string;
  icon: string;
  color: string;
  isActive: boolean;
}

interface ExperimentalUserManagerProps {
  onUserAdded?: () => void;
}

export const ExperimentalUserManager: React.FC<ExperimentalUserManagerProps> = ({ onUserAdded }) => {
  const [selectedExperiment, setSelectedExperiment] = useState<string>('venere');
  const [users, setUsers] = useState<ExperimentalUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingUser, setEditingUser] = useState<ExperimentalUser | null>(null);
  
  // Form states
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');

  // Available experiments (mock data for now)
  const experiments: Experiment[] = [
    {
      id: 'venere',
      name: 'Venere',
      code: 'venere',
      description: 'Advanced AI-powered health insights and personalized recommendations',
      icon: '🌙',
      color: 'purple',
      isActive: true
    }
    // Future experiments can be added here
    // {
    //   id: 'athena',
    //   name: 'Athena',
    //   code: 'athena',
    //   description: 'Next-generation diagnostic tools and health analytics',
    //   icon: '⚡',
    //   color: 'blue',
    //   isActive: true
    // }
  ];

  // Fetch current users
  useEffect(() => {
    fetchUsers();
  }, [selectedExperiment]);

  const fetchUsers = async () => {
    try {
      let fetchedUsers: ExperimentalUser[];
      if (selectedExperiment === 'venere') {
        console.log('🌙 Fetching Venere users using feature-type endpoint...');
        fetchedUsers = await AdminService.getExperimentalUsersByFeatureType('venere');
        console.log('🌙 Venere users fetched:', fetchedUsers);
      } else {
        console.log(`🔧 Fetching ${selectedExperiment} users using query parameter endpoint...`);
        // Keep environment as 'dev' for now, will remove from API calls in next step
        fetchedUsers = await AdminService.getExperimentalUsers(selectedExperiment as 'venere' | 'admin', 'dev');
        console.log(`${selectedExperiment} users fetched:`, fetchedUsers);
      }
      setUsers(fetchedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleAddUser = async () => {
    if (!email.trim()) {
      setMessage('❌ Please enter an email address');
      return;
    }

    setLoading(true);
    setMessage(null);

    console.log('🌙 Adding user to Venere experiment:', {
      email: email.trim(),
      featureType: selectedExperiment,
      environment: 'dev', // Keep for now
      notes: notes.trim() || undefined
    });

    try {
      const response = await AdminService.addExperimentalUser({
        email: email.trim(),
        featureType: selectedExperiment as 'venere' | 'admin',
        environment: 'dev', // Keep for now
        notes: notes.trim() || undefined
      });

      console.log('🌙 Add user response:', response);

      if (response.success) {
        setMessage(`✅ ${response.message}`);
        setEmail('');
        setNotes('');
        setShowAddForm(false);
        console.log('🌙 Refreshing user list after successful add...');
        fetchUsers(); // Refresh the list
        onUserAdded?.();
      } else {
        setMessage(`❌ ${response.message}`);
      }
    } catch (error) {
      console.error('🌙 Error adding user:', error);
      setMessage('❌ Failed to add user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user: ExperimentalUser) => {
    setEditingUser(user);
    setEmail(user.email);
    setNotes(user.notes || '');
    setShowAddForm(true);
  };

  const handleUpdateUser = async () => {
    if (!editingUser || !email.trim()) {
      setMessage('❌ Please enter an email address');
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      // For now, we'll remove and re-add the user since we don't have an update endpoint
      // In the future, this should be a proper update API call
      const removeResponse = await AdminService.removeExperimentalUser(editingUser.id);
      if (removeResponse.success) {
        const addResponse = await AdminService.addExperimentalUser({
          email: email.trim(),
          featureType: selectedExperiment as 'venere' | 'admin',
          environment: 'dev', // Keep for now
          notes: notes.trim() || undefined
        });

        if (addResponse.success) {
          setMessage(`✅ User updated successfully`);
          setEmail('');
          setNotes('');
          setShowAddForm(false);
          setEditingUser(null);
          fetchUsers();
          onUserAdded?.();
        } else {
          setMessage(`❌ ${addResponse.message}`);
        }
      } else {
        setMessage(`❌ ${removeResponse.message}`);
      }
    } catch (error) {
      setMessage('❌ Failed to update user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to remove this user?')) {
      return;
    }

    try {
      const response = await AdminService.removeExperimentalUser(userId);
      if (response.success) {
        setMessage(`✅ ${response.message}`);
        fetchUsers(); // Refresh the list
      } else {
        setMessage(`❌ ${response.message}`);
      }
    } catch (error) {
      setMessage('❌ Failed to remove user. Please try again.');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      editingUser ? handleUpdateUser() : handleAddUser();
    }
  };

  const cancelForm = () => {
    setShowAddForm(false);
    setEditingUser(null);
    setEmail('');
    setNotes('');
    setMessage(null);
  };

  const selectedExperimentData = experiments.find(exp => exp.id === selectedExperiment);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Experiments Selection */}
      <div>
        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">
          Available Experiments
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {experiments.map((experiment) => (
            <Card
              key={experiment.id}
              onClick={() => setSelectedExperiment(experiment.id)}
              className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
                selectedExperiment === experiment.id
                  ? 'ring-2 ring-purple-500 bg-purple-50/50 border-purple-200'
                  : 'bg-white/80 backdrop-blur-sm border-0 shadow-sm hover:bg-white/90'
              }`}
            >
              <CardContent className="p-4">
                <div className="flex items-center space-x-3">
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                    selectedExperiment === experiment.id
                      ? 'bg-purple-100'
                      : 'bg-gray-100'
                  }`}>
                    <span className="text-lg">{experiment.icon}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 truncate">{experiment.name}</h4>
                    <p className="text-xs text-gray-600 mt-1 line-clamp-2">{experiment.description}</p>
                  </div>
                  {selectedExperiment === experiment.id && (
                    <div className="h-5 w-5 rounded-full bg-purple-500 flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Selected Experiment Users */}
      {selectedExperimentData && (
        <div className="space-y-4 sm:space-y-6">
          {/* Header with Add Button */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                <span className="text-purple-600 text-lg">{selectedExperimentData.icon}</span>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
                  {selectedExperimentData.name} Users ({users.length})
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Users with access to {selectedExperimentData.name} features
                </p>
              </div>
            </div>
            <Button
              onClick={() => setShowAddForm(!showAddForm)}
              variant={showAddForm ? "outline" : "default"}
              size="sm"
              className="w-full sm:w-auto"
            >
              {showAddForm ? '✕ Cancel' : '➕ Add User'}
            </Button>
          </div>

          {/* Add/Edit Form */}
          {showAddForm && (
            <Card className="bg-white/50 backdrop-blur-sm border-0 shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">
                  {editingUser ? '✏️ Edit' : '➕ Add'} {selectedExperimentData.name} User
                </CardTitle>
                <CardDescription>
                  {editingUser ? 'Update user details' : 'Add a new user to the experimental program'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="experiment-email">Email Address</Label>
                    <Input
                      id="experiment-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="user@example.com"
                      disabled={loading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="experiment-notes">Notes (Optional)</Label>
                    <Input
                      id="experiment-notes"
                      type="text"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Why this user was added"
                      disabled={loading}
                    />
                  </div>
                </div>

                {message && (
                  <div className={`p-3 rounded-lg text-sm border ${
                    message.startsWith('✅') 
                      ? 'bg-green-50 text-green-800 border-green-200' 
                      : 'bg-red-50 text-red-800 border-red-200'
                  }`}>
                    {message}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                  <Button
                    onClick={cancelForm}
                    variant="outline"
                    disabled={loading}
                    className="w-full sm:w-auto"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={editingUser ? handleUpdateUser : handleAddUser}
                    disabled={!email.trim() || loading}
                    className="w-full sm:w-auto"
                  >
                    {loading ? '⏳ Processing...' : (editingUser ? '💾 Update User' : '➕ Add User')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Users List */}
          {users.length > 0 ? (
            <div className="space-y-3">
              {users.map((user) => (
                <Card key={user.id} className="bg-white/80 backdrop-blur-sm border-0 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex items-start space-x-3">
                        <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-purple-600 text-sm">{selectedExperimentData.icon}</span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {user.email}
                          </p>
                          {user.notes && (
                            <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                              {user.notes}
                            </p>
                          )}
                          <div className="flex items-center space-x-2 mt-2">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              {selectedExperimentData.name}
                            </span>
                            <span className="text-xs text-gray-500">
                              Added by {user.addedBy}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2 flex-shrink-0">
                        <Button
                          onClick={() => handleEditUser(user)}
                          variant="outline"
                          size="sm"
                          className="text-xs"
                        >
                          ✏️ Edit
                        </Button>
                        <Button
                          onClick={() => handleRemoveUser(user.id)}
                          variant="outline"
                          size="sm"
                          className="text-xs text-red-600 border-red-200 hover:bg-red-50"
                        >
                          🗑️ Remove
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="bg-white/50 backdrop-blur-sm border-0 shadow-sm">
              <CardContent className="p-8 text-center">
                <div className="text-gray-400 text-4xl mb-4">
                  {selectedExperimentData.icon}
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No {selectedExperimentData.name} Users
                </h3>
                <p className="text-gray-600 text-sm">
                  No users are currently in the {selectedExperimentData.name} program. Add the first user to get started.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}; 